import React, {
  forwardRef,
  useState,
  memo,
  ReactNode,
  ChangeEvent,
  useCallback,
  useMemo,
} from 'react'

import { isString } from '@surglogs/util'

import { Eye, StatusActive, StatusExpired } from '@surglogs-app/icons'
import { Box, FlexBox, Icon, Text } from '@surglogs-app/ui'
import {
  isPasswordNumeric,
  isPasswordLong,
  isPasswordUppercase,
  isPasswordLowercase,
  isPasswordWithSpecialChar,
  isPasswordOk,
} from '@surglogs-app/util'

import { getConditionColor } from '../helpers/getConditionColor'
import { useAccountSettingsTranslation } from '../services'

import { InputShowToggle } from './InputShowToggle'

interface PasswordInputConditionsProps {
  isLong?: boolean
  isUppercase?: boolean
  isLowercase?: boolean
  isWithSpecialChar?: boolean
  isNumeric?: boolean
}

interface PasswordInputProps extends PasswordInputConditionsProps {
  validation?: boolean
  value?: string
  touched?: boolean
  handleChange?: (value: ChangeEvent<HTMLInputElement>) => void
  invalid?: boolean
  children?: ReactNode
  pattern?: any
  onChange?: (event: any) => void
}

const PasswordInputConditions = ({
  isLong,
  isUppercase,
  isLowercase,
  isWithSpecialChar,
  isNumeric,
  invalid = false,
}: PasswordInputConditionsProps & { invalid?: boolean }) => {
  const { t } = useAccountSettingsTranslation()
  const conditions = {
    isLong,
    isUppercase,
    isLowercase,
    isWithSpecialChar,
    isNumeric,
  }

  return (
    <Box {...{ role: 'group', 'aria-label': 'password-conditions', mt: 'md' }}>
      {Object.keys(conditions).map((key) => (
        <FlexBox key={key} {...{ mt: 'md', ai: 'center' }}>
          <FlexBox
            {...{
              ai: 'center',
              gap: 'sm',
              color: getConditionColor(
                !!conditions[key as keyof PasswordInputConditionsProps],
                invalid,
              ),
            }}>
            <Icon
              {...{
                icon: conditions[key as keyof PasswordInputConditionsProps]
                  ? StatusActive
                  : StatusExpired,
                size: 'md',
                color: 'inherit',
              }}
            />
            <Text {...{ color: 'inherit' }}>
              {t(`passwordInput.${key}` as any)}
            </Text>
          </FlexBox>
        </FlexBox>
      ))}
    </Box>
  )
}

export const PasswordInput = memo(
  forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ validation = true, invalid: invalidProp, ...props }, ref) => {
      const [value, setValue] = useState('')
      const [touched, setTouched] = useState(false)
      const [isNumeric, setIsNumeric] = useState(false)
      const [isLong, setIsLong] = useState(false)
      const [isUppercase, setIsUppercase] = useState(false)
      const [isLowercase, setIsLowercase] = useState(false)
      const [isWithSpecialChar, setIsWithSpecialChar] = useState(false)

      const onFocus = useCallback(() => {
        if (!touched) {
          setTouched(true)
        }
      }, [touched])

      const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
          const newValue = isString(e) ? e : e?.target?.value
          const trimmedValue = (newValue || '').trim()
          setValue(newValue)

          if (validation) {
            if (trimmedValue) {
              setIsNumeric(isPasswordNumeric(trimmedValue))
              setIsLong(isPasswordLong(trimmedValue))
              setIsUppercase(isPasswordUppercase(trimmedValue))
              setIsLowercase(isPasswordLowercase(trimmedValue))
              setIsWithSpecialChar(isPasswordWithSpecialChar(trimmedValue))
            } else {
              setIsNumeric(false)
              setIsLong(false)
              setIsUppercase(false)
              setIsLowercase(false)
              setIsWithSpecialChar(false)
            }
          }

          props.onChange?.(e)
        },
        [validation, props],
      )

      const invalid = useMemo(() => {
        if (validation && touched) {
          return !isPasswordOk(value?.trim()) || Boolean(invalidProp)
        }

        return Boolean(invalidProp)
      }, [validation, touched, value, invalidProp])

      return (
        <>
          <InputShowToggle
            {...props}
            {...{
              onChange: handleChange,
              onFocus,
              invalid,
              toggleIcon: <Icon {...{ icon: Eye, variant: 'primary' }} />,
              value,
              ref,
            }}
          />
          {validation ? (
            <PasswordInputConditions
              {...{
                isLong,
                isUppercase,
                isLowercase,
                isWithSpecialChar,
                isNumeric,
                invalid,
              }}
            />
          ) : null}
        </>
      )
    },
  ),
)
